import { FC, useCallback, useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';
import classNames from 'classnames/bind';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiOrderService } from '@api';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { ENewNotificationType } from '@enum';
import { EUserType, TGetOrderById, TOrder } from '@xeppt/xeppt-sdk/types';
import { modalIds } from '@const/modals';
import useModalContext from '@hooks/context/useModalContext';
import { useQueryParams } from '@hooks/helpers/useQueryParams';
import { useNavigate } from 'react-router-dom';
import { useNavigationContext } from '@hooks/context/useNavigationContext';
import { useLocales } from '@hooks/helpers/useLocales';
import Typography from '@components/common/typography';
import { useDebounceValue } from 'usehooks-ts';
import { Icon, TIconType } from '@components/icons';
import Info from '@components/common/info';
import { useUserContext } from '@hooks/context/useUserContext';
import { useIsFirstRender } from '@mantine/hooks';
import Button from '@components/common/button';
import { routes } from '@const/routes';
import FilterOrderSection from '@sections/orders/header';
import TableOrderSection from '@sections/orders/table';

const cx = classNames.bind(styles);

type SortDir = 'newest' | 'oldest';

const OrdersLayout: FC = () => {
    const { user } = useUserContext();
    const navigate = useNavigate();
    const [id] = useQueryParams(['id']);
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebounceValue(search, 300);
    const { onOpen, changeModalData } = useModalContext();
    const isFirstRender = useIsFirstRender();
    const [page, setPage] = useState(1);
    const loader = useRef(null);
    const [orders, setOrders] = useState<TOrder[]>([]);
    const [trigger, setTrigger] = useState(0);
    const { handleBack } = useNavigationContext();
    const { submitLocale } = useLocales();
    const [isOpen, setIsOpen] = useState(false);

    // Order-specific filter state
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [statusChips, setStatusChips] = useState<string[]>([]);
    const [sortDir, setSortDir] = useState<SortDir>('newest');

    const {
        data: ordersResponse,
        isLoading: isOrdersLoading,
        handleRequest: handleGetOrders
    } = useApiQuery({
        isInitialRequest: false,
        method: (request: any) => {
            return apiOrderService.findBusinessOrders({
                date_to: request?.date_to,
                date_from: request?.date_from,
                search: request?.search,
                page_size: 30,
                page,
                order: request?.sortDir === 'oldest' ? 'ASC' : 'DESC',
                status:
                    request?.statuses && request.statuses.length > 0
                        ? request.statuses
                        : undefined
            });
        },
        onSuccess: (data, requestData) => {
            if (!requestData?.isNew) {
                setOrders((state) => [...state, ...data.orders]);
            } else {
                setOrders(data?.orders || []);
            }
        },
        notificationType: ENewNotificationType.TRANSACTION,
        deps: [user],
        condition: user?.type !== EUserType.INDIVIDUAL
    });

    // Fires the initial load for SPA nav, where `user` is already resolved by
    // the time this page mounts: the deps:[user] effect above only fetches on
    // its SECOND run (isInitialRequest flips true after the first), which
    // never comes if `user` doesn't change again post-mount. On a full
    // reload, `user` resolves async after mount, so that effect's own deps
    // change fires the fetch instead. The empty deps array here means this
    // runs exactly once, so the two paths never both fire for the same mount.
    useEffect(() => {
        if (user && user.type !== EUserType.INDIVIDUAL) {
            setPage(1);
            setOrders([]);
            handleGetOrders({
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
                statuses: statusChips,
                sortDir,
                isNew: true
            });
        }
    }, []);

    useEffect(() => {
        if (debouncedSearch.length >= 3) {
            handleGetOrders({
                search: debouncedSearch,
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
                statuses: statusChips,
                sortDir,
                isNew: true
            });
        }
    }, [debouncedSearch]);

    useEffect(() => {
        const loadItems = () => {
            if (orders.length < (ordersResponse?.total || 0) && !isOrdersLoading) {
                handleGetOrders({
                    date_from: dateFrom || undefined,
                    date_to: dateTo || undefined,
                    statuses: statusChips,
                    sortDir
                });
            }
        };
        loadItems();
    }, [trigger]);

    const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
        const target = entries[0];
        if (target.isIntersecting) {
            setTrigger((prev) => prev + 1);
        }
    }, []);

    // Auto-apply filters whenever any filter value changes (after initial render).
    useEffect(() => {
        if (!isFirstRender) {
            setPage(1);
            setOrders([]);
            handleGetOrders({
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
                statuses: statusChips,
                sortDir,
                isNew: true
            });
        }
    }, [dateFrom, dateTo, statusChips, sortDir]);

    useEffect(() => {
        const observer = new IntersectionObserver(handleObserver, {
            root: null,
            rootMargin: '20px',
            threshold: 0
        });
        if (loader.current) {
            observer.observe(loader.current);
        }
    }, [handleObserver]);

    const handleViewOrder = (order: TGetOrderById) => {
        changeModalData({
            order,
            refetchOrders: () =>
                handleGetOrders({
                    date_from: dateFrom || undefined,
                    date_to: dateTo || undefined,
                    statuses: statusChips,
                    sortDir,
                    isNew: true
                })
        });
        onOpen({
            modalId: modalIds.VIEW_ORDER,
            onClose: () => {
                if (id) {
                    navigate(routes.transactions);
                }
            }
        });
    };

    useApiQuery({
        method: () => apiOrderService.getOrderById(id || ''),
        condition: !!id,
        deps: [id],
        onSuccess: (data) => {
            handleViewOrder(data);
        }
    });

    const activeFilterCount =
        (dateFrom || dateTo ? 1 : 0) + (statusChips.length > 0 ? 1 : 0);

    const statCards: {
        title: string;
        helper: string;
        value: string;
        style: string;
        icon: TIconType;
    }[] = [
        {
            title: 'Paid orders',
            helper: 'The number of paid orders in the selected period',
            value: `${ordersResponse?.total_success_orders || 0}`,
            style: styles.green,
            icon: 'tick_circle'
        },
        {
            title: 'Canceled orders',
            helper: 'The number of orders canceled by the customer before payment in the selected period',
            value: `${ordersResponse?.total_cancelled_orders || 0}`,
            style: styles.total,
            icon: 'close_outlined'
        },
        {
            title: 'Total orders',
            helper: 'The number of orders created in the selected period',
            value: `${ordersResponse?.total || 0}`,
            style: styles.pending,
            icon: 'receipt'
        }
    ];

    return (
        <motion.div {...pageAnimation}>
            <div className={styles.wrapper}>
                <div className={cx([styles.left, { isOpen }])}>
                    <div className={styles.header}>
                        <Button
                            className={styles.back}
                            leftIcon="arrow_left"
                            variant="text-gray"
                            onClick={() => handleBack(routes.dashboard)}>
                            {submitLocale('back')}
                        </Button>
                        <Typography variant="h4">Orders</Typography>
                    </div>
                    <div className={styles.points_wrapper_wrapper}>
                        <div className={styles.points_wrapper}>
                            {statCards.map((item) => (
                                <div
                                    key={item.title}
                                    className={`${styles.point_wrapper} ${item.style}`}>
                                    <div className={styles.icon}>
                                        <Icon name={item.icon} />
                                    </div>
                                    <div className={styles.text_wrapper}>
                                        <div className={styles.title}>
                                            <Typography variant="body4">{item.title}</Typography>
                                            <Info variant="left" content={<>{item.helper}</>} />
                                        </div>
                                        <Typography variant="h3" className={styles.amount}>
                                            {item.value}
                                        </Typography>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <TableOrderSection
                        search={search}
                        onSearch={(val) => setSearch(val)}
                        orders={orders}
                        toggleFilter={() => setIsOpen((state) => !state)}
                        isOrdersLoading={isOrdersLoading}
                        activeFilterCount={activeFilterCount}
                        refetchOrders={() =>
                            handleGetOrders({
                                date_from: dateFrom || undefined,
                                date_to: dateTo || undefined,
                                statuses: statusChips,
                                sortDir,
                                isNew: true
                            })
                        }
                    />
                    <div ref={loader} />
                </div>
                {isOpen && <div className={styles.overlay} onClick={() => setIsOpen(false)} />}
                <div className={cx([styles.right, { isOpen }])}>
                    <FilterOrderSection
                        toggleFilter={() => setIsOpen((state) => !state)}
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        statusChips={statusChips}
                        sortDir={sortDir}
                        onDateFrom={(v) => setDateFrom(v)}
                        onDateTo={(v) => setDateTo(v)}
                        onToggleStatus={(s) =>
                            setStatusChips((prev) =>
                                prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
                            )
                        }
                        onSortDir={(v) => setSortDir(v)}
                    />
                </div>
            </div>
        </motion.div>
    );
};

export default OrdersLayout;
